import { useEffect, useState } from "react";
import { Loader2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Which strategies go into the PDF (Drew, 2026-11-04: "when you click export, it gives
// you a pop-up and asks you to click which strategies you want to include. With the
// baseline."). Every strategy starts ticked; All strategies ticks or unticks the lot;
// Baseline is always in, because every number is measured against it.

export function ExportPickDialog({ open, onOpenChange, names, busy, onExport }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Strategy 1 · LTCG", in page order. */
  names: string[];
  busy: boolean;
  /** The ticked strategies, as indexes into `names`, in page order. */
  onExport: (picked: number[]) => void;
}) {
  const [ticked, setTicked] = useState<boolean[]>(() => names.map(() => true));
  // Each time the window opens it starts with everything ticked.
  useEffect(() => { if (open) setTicked(names.map(() => true)); }, [open, names.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const count = ticked.filter(Boolean).length;
  const all = count === names.length;
  const toggle = (i: number) => setTicked((t) => t.map((v, k) => (k === i ? !v : v)));
  const picked = ticked.flatMap((v, i) => (v ? [i] : []));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="export-pick">
        <DialogHeader>
          <DialogTitle>Export PDF</DialogTitle>
          <DialogDescription>Pick the strategies to include. Baseline is always in.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-1 rounded-lg border p-1.5">
          <label className="flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-sm font-semibold hover:bg-muted/60">
            <Checkbox
              checked={all ? true : count === 0 ? false : "indeterminate"}
              onCheckedChange={() => setTicked(names.map(() => !all))}
              aria-label="All strategies"
            />
            All strategies
          </label>
          <div className="mx-2.5 border-t" />
          <div className="flex items-center gap-3 px-2.5 py-2 text-sm text-muted-foreground">
            <Checkbox checked disabled aria-label="Baseline" />
            Baseline <span className="text-xs">· always included</span>
          </div>
          {names.map((n, i) => (
            <label key={n} className="flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-sm hover:bg-muted/60">
              <Checkbox checked={ticked[i] ?? true} onCheckedChange={() => toggle(i)} aria-label={n} />
              {n}
            </label>
          ))}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onExport(picked)} disabled={count === 0 || busy} className="gap-2" data-testid="export-pick-go">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            Export {count === names.length ? "all" : count} {count === 1 ? "strategy" : "strategies"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
